import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import { format } from 'date-fns';
import Pagination from './Pagination';

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-green-100 text-green-700',
  POST: 'bg-blue-100 text-blue-700',
  PUT: 'bg-amber-100 text-amber-700',
  PATCH: 'bg-amber-100 text-amber-700',
  DELETE: 'bg-red-100 text-red-700',
};

function statusColor(code: number): string {
  if (code < 300) return 'text-green-600';
  if (code < 400) return 'text-blue-600';
  if (code < 500) return 'text-amber-600';
  return 'text-red-600';
}

function durationColor(ms: number): string {
  if (ms < 100) return 'text-green-600';
  if (ms < 500) return 'text-amber-600';
  return 'text-red-600';
}

const ActivityLogTab: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filters change
  const handleMethodChange = useCallback((v: string) => { setMethod(v); setPage(1); }, []);
  const handleStatusChange = useCallback((v: string) => { setStatus(v); setPage(1); }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['activity-log', page, pageSize, debouncedSearch, method, status],
    queryFn: () => adminAPI.getActivityLog({ page, limit: pageSize, search: debouncedSearch, method, status }),
    refetchInterval: autoRefresh ? 10_000 : false,
  });

  const { entries = [], total = 0, pageSize: serverPageSize = 50 } = data ?? {};
  const effectivePageSize = serverPageSize || pageSize;
  const totalPages = Math.ceil(total / effectivePageSize);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder={t('admin.searchPathUserError')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm w-64 focus:ring-primary-500 focus:border-primary-500"
        />
        <select
          value={method}
          onChange={(e) => handleMethodChange(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">{t('admin.allMethods')}</option>
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
          <option value="DELETE">DELETE</option>
        </select>
        <select
          value={status}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">{t('admin.allStatuses')}</option>
          <option value="2xx">{t('admin.status2xx')}</option>
          <option value="4xx">{t('admin.status4xx')}</option>
          <option value="5xx">{t('admin.status5xx')}</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 ml-auto cursor-pointer select-none">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          {t('admin.autoRefresh')}
        </label>
      </div>

      <p className="text-sm text-gray-500">{total} total entr{total !== 1 ? 'ies' : 'y'}</p>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>{t('admin.noActivityEntries')}</p>
        </div>
      ) : (
        <>
          {/* Desktop: Table */}
          <div className="hidden lg:block overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">{t('admin.time')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('admin.method')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('admin.path')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('admin.status')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('admin.duration')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('admin.user')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('admin.ip')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {entries.map((entry) => (
                  <React.Fragment key={entry.id}>
                    <tr
                      onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm text-gray-500">
                        {format(new Date(entry.createdAt), 'dd MMM HH:mm:ss')}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-sm">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${METHOD_COLORS[entry.method] || 'bg-gray-100 text-gray-700'}`}>
                          {entry.method}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900 max-w-xs truncate font-mono text-xs">
                        {entry.path}
                      </td>
                      <td className={`whitespace-nowrap px-3 py-3 text-sm font-medium ${statusColor(entry.statusCode)}`}>
                        {entry.statusCode}
                      </td>
                      <td className={`whitespace-nowrap px-3 py-3 text-sm ${durationColor(entry.durationMs)}`}>
                        {entry.durationMs}ms
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-700">
                        {entry.userName || <span className="text-gray-300">-</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 font-mono text-xs">
                        {entry.ip || '-'}
                      </td>
                    </tr>
                    {expandedId === entry.id && (
                      <tr>
                        <td colSpan={7} className="px-4 py-3 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            {entry.body && (
                              <div>
                                <span className="font-medium text-gray-700">{t('admin.body')}</span>
                                <pre className="mt-1 p-2 bg-white border rounded text-xs overflow-x-auto max-h-40">{entry.body}</pre>
                              </div>
                            )}
                            {entry.error && (
                              <div>
                                <span className="font-medium text-red-600">{t('admin.error')}</span>
                                <pre className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-xs overflow-x-auto max-h-40">{entry.error}</pre>
                              </div>
                            )}
                            {entry.userAgent && (
                              <div className="md:col-span-2">
                                <span className="font-medium text-gray-700">{t('admin.userAgent')}</span>
                                <p className="mt-1 text-xs text-gray-500 break-all">{entry.userAgent}</p>
                              </div>
                            )}
                            {entry.userId && (
                              <div>
                                <span className="font-medium text-gray-700">{t('admin.userId')}</span>
                                <p className="mt-1 text-xs text-gray-500 font-mono">{entry.userId}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile/Tablet: Cards */}
          <div className="lg:hidden space-y-3">
            {entries.map((entry) => (
              <div
                key={entry.id}
                onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-2 cursor-pointer active:bg-gray-50"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${METHOD_COLORS[entry.method] || 'bg-gray-100 text-gray-700'}`}>
                      {entry.method}
                    </span>
                    <span className={`text-sm font-medium ${statusColor(entry.statusCode)}`}>
                      {entry.statusCode}
                    </span>
                    <span className={`text-xs ${durationColor(entry.durationMs)}`}>
                      {entry.durationMs}ms
                    </span>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {format(new Date(entry.createdAt), 'dd MMM HH:mm:ss')}
                  </span>
                </div>
                <div className="text-sm text-gray-900 font-mono text-xs break-all">
                  {entry.path}
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{entry.userName || '-'}</span>
                  <span className="font-mono">{entry.ip || '-'}</span>
                </div>
                {expandedId === entry.id && (
                  <div className="pt-2 mt-2 border-t border-gray-100 space-y-2">
                    {entry.body && (
                      <div>
                        <span className="font-medium text-gray-700 text-xs">{t('admin.body')}</span>
                        <pre className="mt-1 p-2 bg-gray-50 border rounded text-xs overflow-x-auto max-h-40">{entry.body}</pre>
                      </div>
                    )}
                    {entry.error && (
                      <div>
                        <span className="font-medium text-red-600 text-xs">{t('admin.error')}</span>
                        <pre className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-xs overflow-x-auto max-h-40">{entry.error}</pre>
                      </div>
                    )}
                    {entry.userAgent && (
                      <div>
                        <span className="font-medium text-gray-700 text-xs">{t('admin.userAgent')}</span>
                        <p className="mt-1 text-xs text-gray-500 break-all">{entry.userAgent}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        pageSizeOptions={[25, 50, 100]}
      />
    </div>
  );
};

export default ActivityLogTab;
